import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageTitle from '../components/common/PageTitle';
import { Mail, Phone, MapPin, Send, AlertCircle, CheckCircle } from 'lucide-react';

const ContactPage: React.FC = () => {
  const { t, isHindi } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    document.title = isHindi ? 'संपर्क करें | लीगल नेस्ट' : 'Contact Us | Legal Nest';
  }, [isHindi]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!name || !email || !subject || !message) {
      setStatus('error');
      setStatusMessage(isHindi ? 'कृपया सभी क्षेत्रों को भरें।' : 'Please fill in all fields.');
      return;
    }
    
    setStatus('loading');
    
    try {
      // Submit to Formspree
      const response = await fetch('https://formspree.io/f/mgvyzozb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          subject,
          message
        }),
      });
      
      if (response.ok) {
        // Reset form on success
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
        setStatus('success');
        setStatusMessage(isHindi 
          ? 'आपका संदेश सफलतापूर्वक भेज दिया गया है। हम जल्द ही आपसे संपर्क करेंगे।' 
          : 'Your message has been sent successfully. We will get back to you soon.');
        
        // Reset form status after 5 seconds
        setTimeout(() => {
          setStatus('idle');
          setStatusMessage('');
        }, 5000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setStatus('error');
      setStatusMessage(isHindi 
        ? 'आपका संदेश भेजने में एक त्रुटि हुई। कृपया बाद में पुनः प्रयास करें।' 
        : 'There was an error sending your message. Please try again later.');
    }
  };

  // The rest of your component remains the same
  return (
    <div className="container-custom py-10">
      <PageTitle title={t('contact_us')} />
      
      <div className="max-w-5xl mx-auto">
        <p className="text-center text-lg text-neutral-600 mb-12">
          {isHindi 
            ? 'किसी भी प्रश्न, सुझाव या सहयोग के लिए हमसे संपर्क करें।'
            : 'Get in touch with us for any questions, suggestions, or collaborations.'}
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact information */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h3 className="font-serif font-bold text-xl mb-6">
                {isHindi ? 'संपर्क जानकारी' : 'Contact Information'}
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                    <Mail className="text-primary-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-1">
                      {isHindi ? 'ईमेल' : 'Email'}
                    </h4>
                    <a href="mailto:info@legalnest.live" className="text-neutral-600 hover:text-primary-600">
                      info@legalnest.live
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                    <Phone className="text-primary-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-1">
                      {isHindi ? 'फोन' : 'Phone'}
                    </h4>
                    <a href="tel:+911234567890" className="text-neutral-600 hover:text-primary-600">
                      +91 123 456 7890
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                    <MapPin className="text-primary-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-1">
                      {isHindi ? 'पता' : 'Address'}
                    </h4>
                    <address className="text-neutral-600 not-italic">
                      {isHindi ? (
                        <>
                          लीगल नेस्ट हब<br />
                          10वां फ्लोर, टेक सेंटर<br />
                          सेक्टर , गुड़गांव<br />
                          हरयाणा, भारत -
                        </>
                      ) : (
                        <>
                          Legal Nest Hub<br />
                          10th Floor, Tech Center<br />
                          Sector , Gurugram<br />
                          Haryana, India - 
                        </>
                      )}
                    </address>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-serif font-bold text-xl mb-4">
                {isHindi ? 'कार्य समय' : 'Working Hours'}
              </h3>
              <p className="text-neutral-600 mb-4">
                {isHindi ? 'सोमवार - शुक्रवार: सुबह 9:00 - शाम 6:00' : 'Monday - Friday: 9:00 AM - 6:00 PM'}
              </p>
              <p className="text-neutral-600">
                {isHindi ? 'शनिवार - रविवार: बंद' : 'Saturday - Sunday: Closed'}
              </p>
            </div>
          </div>
          
          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-serif font-bold text-xl mb-6">
                {isHindi ? 'हमें संदेश भेजें' : 'Send Us A Message'}
              </h3>
              
              {status === 'success' ? (
                <div className="p-6 bg-green-50 border border-green-100 rounded-md flex items-center">
                  <CheckCircle className="text-green-600 mr-3 flex-shrink-0" size={24} />
                  <p className="text-green-700">{statusMessage}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-100 rounded-md flex items-center">
                      <AlertCircle className="text-red-600 mr-3 flex-shrink-0" size={20} />
                      <p className="text-red-700">{statusMessage}</p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">
                        {isHindi ? 'नाम' : 'Name'} *
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="form-input w-full px-4 py-3 border border-neutral-300 rounded-md"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                        {isHindi ? 'ईमेल' : 'Email'} *
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input w-full px-4 py-3 border border-neutral-300 rounded-md"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-neutral-700 mb-1">
                      {isHindi ? 'विषय' : 'Subject'} *
                    </label>
                    <input
                      id="subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="form-input w-full px-4 py-3 border border-neutral-300 rounded-md"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">
                      {isHindi ? 'संदेश' : 'Message'} *
                    </label>
                    <textarea
                      id="message"
                      rows={6}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="form-input w-full px-4 py-3 border border-neutral-300 rounded-md"
                      required
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="btn btn-primary w-full flex items-center justify-center py-3"
                  >
                    {status === 'loading' ? (
                      <>
                        <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full"></div>
                        {isHindi ? 'भेज रहा है...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <Send size={16} className="mr-2" />
                        {isHindi ? 'संदेश भेजें' : 'Send Message'}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        
        {/* Map */}
        <div className="mt-12 rounded-lg overflow-hidden shadow-md h-80 bg-neutral-200">
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224368.3931459528!2d76.94796853386722!3d28.527580356391777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x37205b715389640!2sGurugram%2C%20Haryana!5e0!3m2!1sen!2sin!4v1665066034790!5m2!1sen!2sin" 
            width="100%" 
            height="100%" 
            style={{ border: 0 }} 
            allowFullScreen 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Legal Nest Office Location"
          ></iframe>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;